import { MINIMUM_ORDER, orderState, lei } from './minimum-order-policy.js';

const config = document.getElementById('MinimumOrderConfig');
if (config && !window.minimumOrder49Loaded) {
  window.minimumOrder49Loaded = true;
  const initial = JSON.parse(config.textContent);
  const originalFetch = window.fetch.bind(window);
  const root = initial.root.endsWith('/') ? initial.root : `${initial.root}/`;
  const checkoutSelector = 'button[name="checkout"], input[name="checkout"], [data-minimum-order-checkout]';
  const acceleratedSelector = '.shopify-payment-button, shopify-accelerated-checkout, shopify-accelerated-checkout-cart, .additional-checkout-buttons, .cart__dynamic-checkout-buttons, .minimum-order-express';
  const disabledByGuard = new WeakSet();
  const allowedForms = new WeakSet();
  const pendingCartRequests = new Set();
  let cart = initial.cart;
  let busy = false;
  let verificationError = false;
  let refreshPromise;
  let refreshAgain = false;
  let checkoutPending = false;

  function text(element, value) {
    if (element.textContent !== value) element.textContent = value;
  }

  function renderExpress() {
    const blocked = busy || verificationError || orderState(cart).blocked;
    for (const wrapper of document.querySelectorAll('[data-minimum-order-express]')) {
      const form = wrapper.closest('form');
      const prices = wrapper.querySelector('[data-minimum-order-prices]');
      let productBlocked = false;
      if (prices && !form) productBlocked = true;
      if (prices && form) {
        const values = JSON.parse(prices.textContent);
        const variant = form.querySelector('[name="id"]')?.value;
        const quantity = Math.max(1, Number(form.querySelector('[name="quantity"]')?.value || 1));
        productBlocked = !Number.isFinite(quantity) || !values[variant] || Number(values[variant]) * quantity < MINIMUM_ORDER;
      }
      wrapper.hidden = blocked || productBlocked;
    }
  }

  function render() {
    const state = orderState(cart);
    const blocked = busy || verificationError || state.blocked;
    config.dataset.blocked = String(blocked);
    document.documentElement.dataset.minimumOrderBlocked = String(blocked);
    for (const button of document.querySelectorAll(checkoutSelector)) {
      if (blocked) {
        if (!button.disabled || button.hasAttribute('data-minimum-order-owned-disabled')) disabledByGuard.add(button);
        button.disabled = true;
        button.setAttribute('aria-disabled', 'true');
      } else if (disabledByGuard.has(button) || button.hasAttribute('data-minimum-order-owned-disabled')) {
        button.disabled = false;
        button.removeAttribute('aria-disabled');
        button.removeAttribute('data-minimum-order-owned-disabled');
        disabledByGuard.delete(button);
      }
    }
    for (const notice of document.querySelectorAll('[data-minimum-order-notice]')) {
      const label = notice.querySelector('[data-minimum-order-label]');
      if (label) {
        const value = verificationError
          ? 'Nu putem verifica valoarea coșului. Verifică conexiunea și încearcă din nou.'
          : busy
            ? 'Verificăm valoarea coșului…'
            : state.blocked
              ? `Comanda minimă este 49\u00a0lei. Mai adaugă ${lei(state.remaining)}.`
              : 'Comandă minimă 49\u00a0lei.';
        text(label, value);
      }
      const suggestions = notice.querySelector('[data-minimum-order-recommendations]');
      if (suggestions) suggestions.hidden = !state.blocked && !verificationError;
      const retry = notice.querySelector('[data-minimum-order-retry]');
      if (retry) retry.hidden = !verificationError;
    }
    renderExpress();
  }

  async function readCart() {
    if (pendingCartRequests.size) await Promise.allSettled([...pendingCartRequests]);
    const response = await originalFetch(`${root}cart.js`, { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Cart verification failed');
    const value = await response.json();
    if (!orderState(value).valid || !Array.isArray(value.items)) throw new Error('Invalid cart');
    return value;
  }

  function refresh() {
    if (refreshPromise) { refreshAgain = true; return refreshPromise; }
    busy = true;
    render();
    refreshPromise = (async () => {
      try { cart = await readCart(); verificationError = false; }
      catch { verificationError = true; }
      finally {
        busy = false;
        refreshPromise = undefined;
        render();
        if (refreshAgain) { refreshAgain = false; refresh(); }
      }
      return !verificationError;
    })();
    return refreshPromise;
  }

  function focusNotice() {
    const notice = [...document.querySelectorAll('[data-minimum-order-notice]')].find(node => node.getClientRects().length);
    if (notice) { notice.focus({ preventScroll: true }); notice.scrollIntoView({ block: 'nearest', behavior: 'smooth' }); }
  }

  async function applyPendingQuantities(form) {
    if (!form) return false;
    const fields = [...form.querySelectorAll('input[name="updates[]"], input[name^="updates["]')];
    if (!fields.length) return false;
    const updates = {};
    let changed = false;
    fields.forEach((field, index) => {
      const line = cart.items[index];
      if (!line) return;
      const quantity = Number(field.value);
      if (!Number.isSafeInteger(quantity) || quantity < 0) return;
      updates[line.key] = quantity;
      if (quantity !== line.quantity) changed = true;
    });
    if (!changed) return false;
    busy = true;
    render();
    try {
      const response = await originalFetch(`${root}cart/update.js`, {
        method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ updates }),
      });
      if (!response.ok) throw new Error('Cart update failed');
      cart = await response.json();
      if (!orderState(cart).valid) throw new Error('Invalid updated cart');
      return true;
    } finally { busy = false; render(); }
  }

  async function attemptCheckout(control, form, href) {
    if (checkoutPending) return;
    checkoutPending = true;
    try {
      if (form && !form.reportValidity()) return;
      if (!await refresh()) { focusNotice(); return; }
      const changed = await applyPendingQuantities(form);
      render();
      if (orderState(cart).blocked || verificationError) {
        if (changed) { window.location.assign(`${root}cart`); return; }
        focusNotice(); return;
      }
      if (form) {
        allowedForms.add(form);
        if (control instanceof HTMLButtonElement || control instanceof HTMLInputElement) form.requestSubmit(control);
        else form.requestSubmit();
      } else if (href) window.location.assign(href);
    } catch {
      verificationError = true; render(); focusNotice();
    } finally { checkoutPending = false; }
  }

  document.addEventListener('click', event => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    if (target.closest('[data-minimum-order-retry]')) { event.preventDefault(); refresh(); return; }
    const button = target.closest(checkoutSelector);
    const anchor = target.closest('a[href]');
    const checkoutHref = anchor && /\/checkouts?(\/|$)/.test(new URL(anchor.href, location.href).pathname) ? anchor.href : null;
    if (!button && !checkoutHref) return;
    event.preventDefault(); event.stopImmediatePropagation();
    attemptCheckout(button, button?.form, checkoutHref);
  }, true);

  document.addEventListener('submit', event => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (allowedForms.has(form)) { allowedForms.delete(form); return; }
    const submitter = event.submitter;
    const checkout = submitter?.matches(checkoutSelector) || /\/checkouts?(\/|$)/.test(new URL(form.action, location.href).pathname);
    if (!checkout) return;
    event.preventDefault(); event.stopImmediatePropagation();
    attemptCheckout(submitter, form);
  }, true);

  // Observe the themes' existing cart requests without replacing their response bodies.
  window.fetch = function (...args) {
    const request = args[0];
    const url = new URL(typeof request === 'string' || request instanceof URL ? request : request.url, location.href);
    const method = String(args[1]?.method || request?.method || 'GET').toUpperCase();
    const mutation = url.origin === location.origin && method !== 'GET' && /\/cart\/(add|change|update|clear)(\.js)?$/.test(url.pathname);
    if (mutation) { busy = true; render(); }
    const promise = originalFetch(...args);
    if (mutation) {
      pendingCartRequests.add(promise);
      promise.then(() => { pendingCartRequests.delete(promise); refresh(); }, () => { pendingCartRequests.delete(promise); refresh(); });
    }
    return promise;
  };

  const observer = new MutationObserver(records => {
    const restoredCheckout = records.some(record => record.type === 'attributes' && record.target.matches(checkoutSelector) && !record.target.disabled && (busy || verificationError || orderState(cart).blocked));
    const addedControls = records.some(record => [...record.addedNodes].some(node => node instanceof Element && (
      node.matches(checkoutSelector) || node.matches('[data-minimum-order-notice], [data-minimum-order-express]') || node.querySelector(`${checkoutSelector}, [data-minimum-order-notice], [data-minimum-order-express]`)
    )));
    if (addedControls || restoredCheckout) render();
  });
  observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['disabled'] });
  document.addEventListener('change', () => renderExpress());
  window.addEventListener('online', refresh);
  window.addEventListener('pageshow', event => { if (event.persisted) refresh(); });
  render();
}
